import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Trash2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail, eur } from "@/lib/api";
import { toast } from "sonner";

export default function BudgetModal({ open, onClose, onSaved, budgets, categories, month }) {
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!category || !amount) {
      toast.error("Seleziona categoria e importo");
      return;
    }
    setBusy(true);
    try {
      await api.post("/budgets", { category, amount: parseFloat(amount), structure_id: null, month });
      toast.success("Budget impostato");
      setCategory(""); setAmount("");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/budgets/${id}`);
    toast.success("Budget rimosso");
    onSaved();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            data-testid="modal-budgets"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[#111A2E] border border-white/15 rounded-2xl p-6 shadow-2xl max-w-lg w-full text-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold">Budget Mensili & Avvisi</h3>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 max-h-56 overflow-y-auto mb-5 pr-1">
              {budgets.map((b) => {
                const pct = b.amount > 0 ? Math.min((b.spent / b.amount) * 100, 100) : 0;
                const over = b.spent > b.amount;
                const warn = pct >= 80;
                const barColor = over ? "bg-rose-500" : warn ? "bg-amber-500" : "bg-emerald-500";
                return (
                  <div key={b.id} className="bg-[#0E1524] border border-white/10 rounded-lg px-3 py-3" data-testid="budget-row">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium flex items-center gap-1.5">
                        {(over || warn) && <AlertTriangle className={`w-3.5 h-3.5 ${over ? "text-rose-400" : "text-amber-400"}`} />}
                        {b.category}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-slate-400">{eur(b.spent)} / {eur(b.amount)}</span>
                        <button onClick={() => remove(b.id)} className="text-slate-500 hover:text-rose-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden" data-testid="budget-progress-bar">
                      <div className={`h-full ${barColor} transition-all rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                    {over && <p className="text-xs text-rose-400 mt-1.5">Superato di {eur(b.spent - b.amount)}</p>}
                  </div>
                );
              })}
              {budgets.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">Nessun budget impostato per questo mese.</p>
              )}
            </div>

            <div className="border-t border-white/10 pt-4 space-y-3">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Nuovo budget (uscite)</p>
              <select
                data-testid="input-budget-category"
                className="w-full bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm cursor-pointer"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Seleziona categoria</option>
                {categories.expense?.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="flex gap-3">
                <input
                  data-testid="input-budget-amount"
                  type="number"
                  className="flex-1 bg-[#0E1524] border border-white/15 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm font-mono"
                  placeholder="Limite € / mese"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <button
                  data-testid="btn-add-budget"
                  onClick={add}
                  disabled={busy}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-5 py-2 rounded-lg transition-all active:scale-[0.98] disabled:opacity-60 flex items-center gap-2"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  Imposta
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
